import "./InvoicesModal.scss"

import {
  CELL_VARIANT,
  Filter,
  FilterPosition,
  FilterVariant,
  TableCell,
  TableData,
  TableEmptyMessage,
} from "@skylead/component-library"
import moment from "moment"
// import PropTypes from "prop-types"
import React, { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"

import { HELP_LINKS } from "../../../constants/help-center"
import { getAllInvoices, updateFormField, updateFormFields } from "../../../redux/actions"
import { useGlobalTranslation } from "../../../utils/hook"
import LangText from "../../atoms/LangText"

const InvoicesModal = ({ teamsWithPayment = [] }) => {
  const { tt } = useGlobalTranslation()
  const [invoices, setInvoices] = useState([])
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(0)
  const [invoicesFilterSearch, setInvoicesFilterSearch] = useState("")
  const [showSelectCampaignFilter, setShowSelectCampaignFilter] = useState(false)
  const maxOnPage = 10

  const { selectedRows, formData, whitelabelId } = useSelector(state => ({
    selectedRows: state.forms.formData.selectedRows,
    formData: state.forms.formData,
    whitelabelId: state.user.profile.whitelabelId,
  }))

  const onInvoiceFilterSearch = value => {
    setInvoicesFilterSearch(value?.toLowerCase().trim())
  }

  const allInvoiceOptions = useMemo(() => {
    const options = []

    teamsWithPayment.forEach(({ teamId, teamName, initialUserId }) => {
      options.push({ value: teamId, name: teamName, initialUserId })
    })

    return options
  }, [teamsWithPayment])

  const options = useMemo(() => {
    return allInvoiceOptions.filter(option => option.name?.includes(invoicesFilterSearch))
  }, [invoicesFilterSearch, allInvoiceOptions])

  const [invoiceTeam, setInvoiceTeam] = useState(options.length ? options[0] : null)

  const dispatch = useDispatch()

  useEffect(() => {
    if (allInvoiceOptions.length) {
      setInvoiceTeam(allInvoiceOptions[0])
    }
  }, [allInvoiceOptions])

  useEffect(() => {
    const getInvoices = async () => {
      if (teamsWithPayment.length) {
        setLoading(true)
        const query = `?limit=${maxOnPage}`
        const res = await dispatch(getAllInvoices(invoiceTeam.initialUserId, query))
        setInvoices(res.data?.items)
        setHasMore(res.data?.hasMore)
        setLoading(false)
      }
    }
    getInvoices()
  }, [invoiceTeam])

  const changePage = async incomingPage => {
    let query = ""
    if (incomingPage === 0) {
      query = `?limit=${maxOnPage}`
    } else if (incomingPage > page) {
      query = `?limit=${maxOnPage}&startingAfterId=${invoices[invoices.length - 1].id}`
    } else {
      query = `?limit=${maxOnPage}&endingBeforeId=${invoices[0].id}`
    }
    setLoading(true)
    const res = await dispatch(getAllInvoices(invoiceTeam.initialUserId, query))
    setInvoices(res?.data?.items)
    setHasMore(res?.data?.hasMore)
    setPage(incomingPage)
    setLoading(false)
  }

  const handleDownloadClick = invoice => {
    const link = document.createElement("a")
    link.href = invoice.link
    link.download = invoice.link.split("/").pop() // Set filename based on the link
    link.click()
  }

  const campaignColumns = useMemo(() => {
    const campaignCol = {
      left: [],
      center: [],
      right: [],
    }

    campaignCol.center = [
      {
        id: "2",
        name: tt("date"),
        key: "invoiceDates",
        className: "invoice-date-column table-cell-padding",
        component: item => {
          return (
            <TableCell
              cellType={CELL_VARIANT.TEXT}
              text={moment(item.date).format("DD.MM.YYYY.")}
              tt={tt}
            />
          )
        },
      },
      {
        id: "3",
        name: tt("invoice-url"),
        key: "invoiceUrls",
        className: "invoice-url-column table-cell-padding",
        component: item => {
          return (
            <TableCell
              cellType={CELL_VARIANT.TEXT}
              text={item.link}
              icon="download-20"
              iconClassname="download-icon"
              inputButton
              iconOnClick={() => handleDownloadClick(item)}
              tt={tt}
            />
          )
        },
      },
    ]

    return campaignCol
  }, [])

  const handleOnSelectedRowsChange = sr => {
    dispatch(updateFormField("selectedRows", sr))
  }

  return (
    <div className="download-invoices">
      <div className="download-invoices__header">
        <p className="small invoice-text">
          <LangText langKey="invoice.modal.pick-team.msg" />
        </p>
      </div>
      <div className="download-invoices__body">
        <Filter
          variant={FilterVariant.MENU}
          tt={tt}
          position={FilterPosition.RIGHT_BOTTOM}
          icon="speakerphone-20"
          label={
            invoiceTeam && invoiceTeam.name
              ? invoiceTeam.name.length > 35
                ? `${invoiceTeam.name.substring(0, 35)}...`
                : invoiceTeam.name
              : tt("team-name")
          }
          iconOnEnd="chevron-down-20"
          data={options}
          onSelect={selectedInvoiceTeam => {
            setInvoiceTeam(selectedInvoiceTeam)
          }}
          onChange={onInvoiceFilterSearch}
          filterSearchPlaceholder={tt("search-campaigns-placeholder")}
          isOpen={showSelectCampaignFilter}
          setIsOpen={setShowSelectCampaignFilter}
          characterTooltipLimit={20}
        />
        <div>
          <TableData
            data={invoices}
            columns={campaignColumns}
            paginationSize="small"
            noFixedPagination
            resetRowsOnFilter={false}
            count={invoices?.length}
            noTopHeight
            pagesInfo={false}
            hasMore={hasMore}
            page={page}
            changePage={changePage}
            loading={loading}
            emptyState={
              <TableEmptyMessage
                icon="illustration-style-03"
                svgClassName="illustration-svg-empty-table"
                className="empty-invoices"
                illustrationMessage={tt("invoice.modal.table-empty-message.msg")}
                tt={tt}
              />
            }
            selectedRows={selectedRows}
            dispatch={dispatch}
            updateFormField={updateFormField}
            teamRolesHelpLink={HELP_LINKS.WHAT_ARE_TEAM_ROLES_AND_HOW_TO_ASSIGN_THEM}
            updateFormFields={updateFormFields}
            formData={formData}
            whitelabelId={whitelabelId}
            tt={tt}
            onSelectedRowsChange={handleOnSelectedRowsChange}
          />
        </div>
      </div>
    </div>
  )
}

// InvoicesModal.propTypes = {
//   teamsWithPayment: PropTypes.instanceOf(Array),
// }

export default InvoicesModal
